import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import { filterRowsForCurrentScope, getCenterScopedUrl } from "../../utils/centerScope";
import useDataStore from "../../store/dataStore";

const BASE_URL = "https://mis.empowerplus.in";

const emptyForm = {
    beneficiaryId: "",
    assessment: false,
    attendancePercentage: "",
    assessmentConducted: false,
    assessmentThroughMode: "",
    assessmentAgencyId: "",
    assessmentAgencyName: "",
    assessorId: "",
    assessmentDate: "",
    beneficiaryAssessmentStatus: "",
    assessmentPercentage: ""
};

const yesNo = (value) => (value ? "Yes" : "No");

const toInputDate = (value) => {
    if (!value) return "";
    return String(value).slice(0, 10);
};

const numberOrNull = (value) => {
    if (value === "" || value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
};

export default function ExternalAssessment() {
    const token = localStorage.getItem("token");
    const store = useDataStore();
    const CACHE_KEY = "externalAssessments";

    const [msg, setMsg] = useState({ type: "", text: "" });
    const [rows, setRows] = useState(() => store.getRows(CACHE_KEY));
    const [loading, setLoading] = useState(false);
    const [allLoaded, setAllLoaded] = useState(() => store.isAllLoaded(CACHE_KEY));
    const [loadingAll, setLoadingAll] = useState(false);
    const [search, setSearch] = useState("");
    const [visibleCount, setVisibleCount] = useState(100);
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [totalCount, setTotalCount] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editingBeneficiaryId, setEditingBeneficiaryId] = useState("");
    const [form, setForm] = useState(emptyForm);

    const flash = (type, text) => {
        setMsg({ type, text });
    };

    const authHeaders = useMemo(
        () => ({
            Authorization: `Bearer ${token}`
        }),
        [token]
    );

    const jsonHeaders = useMemo(
        () => ({
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
        }),
        [token]
    );

    const safeJson = async (res) => {
        const text = await res.text();
        try {
            return text ? JSON.parse(text) : [];
        } catch {
            return [];
        }
    };

    const fetchExternalAssessments = useCallback(async () => {
        if (store.isCached(CACHE_KEY)) {
            setRows(store.getRows(CACHE_KEY));
            setAllLoaded(store.isAllLoaded(CACHE_KEY));
            return;
        }
        setLoading(true);

        try {
            const scopedUrl = getCenterScopedUrl(BASE_URL, "/api/external-assessments");
            const res = await fetch(scopedUrl + (scopedUrl.includes("?") ? "&" : "?") + "page=0&size=100", {
                headers: authHeaders
            });

            const data = await safeJson(res);
            const list = data.content || data || [];
            setRows(list);
            store.setRows(CACHE_KEY, list, false);
            setAllLoaded(false);
        } catch {
            setRows([]);
            flash("err", "Failed to load external assessments");
        } finally {
            setLoading(false);
        }
    }, [authHeaders]);

    const fetchAllExternalAssessments = async () => {
        setLoadingAll(true);
        try {
            const res = await fetch(getCenterScopedUrl(BASE_URL, "/api/external-assessments"), {
                headers: authHeaders
            });
            const data = await safeJson(res);
            const list = data.content || data || [];
            setRows(list);
            store.setRows(CACHE_KEY, list, true);
            setAllLoaded(true);
        } catch {
            flash("err", "Failed to load all external assessments");
        } finally {
            setLoadingAll(false);
        }
    };

    useEffect(() => {
        fetchExternalAssessments();
    }, [fetchExternalAssessments]);

    useEffect(() => {
        fetch(`${BASE_URL}/api/dashboard/counts`, { headers: authHeaders })
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d) setTotalCount(d.externalAssessments ?? null); })
            .catch(() => { });
    }, []);

    useEffect(() => { setVisibleCount(100); }, [search]);

    const scopedRows = useMemo(() => filterRowsForCurrentScope(rows), [rows]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();

        if (!q) return scopedRows;

        return scopedRows.filter((row) =>
            row.beneficiaryId?.toLowerCase().includes(q) ||
            row.beneficiaryName?.toLowerCase().includes(q) ||
            row.assessorId?.toLowerCase().includes(q) ||
            row.assessorName?.toLowerCase().includes(q) ||
            row.assessmentAgencyName?.toLowerCase().includes(q) ||
            row.beneficiaryAssessmentStatus?.toLowerCase().includes(q)
        );
    }, [scopedRows, search]);

    const resetForm = () => {
        setForm(emptyForm);
        setEditingBeneficiaryId("");
    };

    const openCreate = () => {
        resetForm();
        setModalOpen(true);
    };

    const openEdit = async (beneficiaryId) => {
        if (!beneficiaryId) return;

        setSaving(false);
        setModalOpen(true);
        setEditingBeneficiaryId(beneficiaryId);

        try {
            const res = await fetch(
                `${BASE_URL}/api/external-assessments/${encodeURIComponent(beneficiaryId)}`,
                { headers: authHeaders }
            );

            if (!res.ok) {
                throw new Error("Failed to fetch record");
            }

            const data = await safeJson(res);

            setForm({
                beneficiaryId: data.beneficiaryId || "",
                assessment: !!data.assessment,
                attendancePercentage: data.attendancePercentage ?? "",
                assessmentConducted: !!data.assessmentConducted,
                assessmentThroughMode: data.assessmentThroughMode || "",
                assessmentAgencyId: data.assessmentAgencyId || "",
                assessmentAgencyName: data.assessmentAgencyName || "",
                assessorId: data.assessorId || "",
                assessmentDate: toInputDate(data.assessmentDate),
                beneficiaryAssessmentStatus: data.beneficiaryAssessmentStatus || "",
                assessmentPercentage: data.assessmentPercentage ?? ""
            });
        } catch {
            flash("err", "Failed to load record details");
            setModalOpen(false);
            resetForm();
        }
    };

    const handleFormChange = (event) => {
        const { name, value } = event.target;

        if (name === "assessment" || name === "assessmentConducted") {
            setForm((prev) => ({
                ...prev,
                [name]: value === "true"
            }));
            return;
        }

        setForm((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const saveExternalAssessment = async () => {
        if (!form.beneficiaryId.trim()) {
            flash("err", "Beneficiary ID is required");
            return;
        }

        setSaving(true);

        try {
            const payload = {
                beneficiaryId: form.beneficiaryId.trim(),
                assessment: !!form.assessment,
                attendancePercentage: numberOrNull(form.attendancePercentage),
                assessmentConducted: !!form.assessmentConducted,
                assessmentThroughMode: form.assessmentThroughMode.trim(),
                assessmentAgencyId: form.assessmentAgencyId.trim(),
                assessmentAgencyName: form.assessmentAgencyName.trim(),
                assessorId: form.assessorId.trim(),
                assessmentDate: form.assessmentDate || null,
                beneficiaryAssessmentStatus: form.beneficiaryAssessmentStatus.trim(),
                assessmentPercentage: numberOrNull(form.assessmentPercentage)
            };

            const res = await fetch(`${BASE_URL}/api/external-assessments`, {
                method: "POST",
                headers: jsonHeaders,
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                throw new Error("Save failed");
            }

            flash("ok", editingBeneficiaryId ? "External assessment updated" : "External assessment created");
            setModalOpen(false);
            resetForm();
            store.invalidate(CACHE_KEY);
            fetchExternalAssessments();
        } catch {
            flash("err", "Failed to save external assessment");
        } finally {
            setSaving(false);
        }
    };

    const deleteExternalAssessment = async (id) => {
        if (!id) {
            flash("err", "Record ID not found");
            return;
        }

        if (!window.confirm("Delete this external assessment record?")) return;

        try {
            const res = await fetch(`${BASE_URL}/api/external-assessments/${id}`, {
                method: "DELETE",
                headers: authHeaders
            });

            if (!res.ok) {
                throw new Error("Delete failed");
            }

            flash("ok", "External assessment deleted");
            store.invalidate(CACHE_KEY);
            fetchExternalAssessments();
        } catch {
            flash("err", "Failed to delete record");
        }
    };

    const CHUNK_SIZE = 500;
    const PARALLEL = 2;

    const uploadExcel = async () => {
        if (!file) return flash("err", "Select Excel file");
        let dataRows;
        let headerRow;
        try {
            const buffer = await file.arrayBuffer();
            const wb = XLSX.read(buffer, { type: "array" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
            headerRow = raw[0];
            dataRows = raw.slice(1);
        } catch (err) {
            return flash("err", `Cannot read file: ${err.message}`);
        }
        if (!dataRows.length) return flash("err", "Excel file is empty");
        const totalRows = dataRows.length;
        const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);
        setUploading(true);
        setUploadProgress({ uploaded: 0, total: totalRows, chunk: 0, totalChunks });
        let uploadedSoFar = 0;
        let totalInserted = 0;
        let totalSkipped = 0;
        let failed = false;
        let firstError = "";
        const uploadOneChunk = async (i) => {
            const chunkRows = [headerRow, ...dataRows.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE)];
            const res = await fetch(`${BASE_URL}/api/external-assessments/upload-json?actor=Rahul`, {
                method: "POST",
                headers: { ...authHeaders, "Content-Type": "application/json" },
                body: JSON.stringify(chunkRows),
            });
            if (res.status === 413) throw new Error(`Chunk ${i + 1}: Payload too large (413)`);
            const text = await res.text();
            let data;
            try { data = JSON.parse(text); } catch { data = text; }
            if (!res.ok) throw new Error(`Chunk ${i + 1}: ${data?.message || `HTTP ${res.status}`}`);
            return { inserted: data?.inserted ?? 0, skipped: data?.skipped ?? 0 };
        };
        for (let batchStart = 0; batchStart < totalChunks && !failed; batchStart += PARALLEL) {
            const batchEnd = Math.min(batchStart + PARALLEL, totalChunks);
            const batchIndices = Array.from({ length: batchEnd - batchStart }, (_, k) => batchStart + k);
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks });
            const results = await Promise.allSettled(batchIndices.map(uploadOneChunk));
            for (const result of results) {
                if (result.status === "fulfilled") {
                    totalInserted += result.value.inserted;
                    totalSkipped += result.value.skipped;
                    uploadedSoFar += result.value.inserted + result.value.skipped;
                } else {
                    failed = true;
                    firstError = result.reason?.message || "Upload failed";
                }
            }
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks, error: failed });
        }
        setUploading(false);
        if (!failed) {
            flash("ok", `Upload complete — Inserted: ${totalInserted.toLocaleString()}, Skipped: ${totalSkipped.toLocaleString()} (${totalChunks} chunks)`);
            setFile(null);
            setUploadProgress(null);
            store.invalidate(CACHE_KEY);
            fetchExternalAssessments();
        } else {
            flash("err", firstError);
            setUploadProgress(prev => prev ? { ...prev, error: true } : null);
        }
    };

    // const downloadTemplate = () => {
    //     const rowsForTemplate = [
    //         [
    //             "Beneficiary ID",
    //             "Beneficiary Name",
    //             "Assessment (Yes/No)",
    //             "Attendance (%)",
    //             "Assessment conducted (Yes/No)",
    //             "Assessment through (Mode)",
    //             "Assessment Agency ID",
    //             "Assessment Agency Name",
    //             "Assessor ID",
    //             "Assessor Name",
    //             "Assessment Date",
    //             "Beneficiary Assessment Status",
    //             "Assessment Percentage"
    //         ],
    //         [
    //             "EP/CN-00001",
    //             "Rahul Kumar",
    //             "Yes",
    //             70,
    //             "Yes",
    //             "Self",
    //             "AA-2001",
    //             "XYZ Assessment Agency",
    //             "EP/AR-00001",
    //             "Ramesh Sir",
    //             "23/03/2024",
    //             "Pass",
    //             83
    //         ]
    //     ];

    //     const worksheet = XLSX.utils.aoa_to_sheet(rowsForTemplate);
    //     const workbook = XLSX.utils.book_new();
    //     XLSX.utils.book_append_sheet(workbook, worksheet, "External Assessment");
    //     XLSX.writeFile(workbook, "external_assessment_template.xlsx");
    // };


    const [exporting, setExporting] = useState(false);

    const exportExcel = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            let data = rows;
            if (!allLoaded) {
                const res = await fetch(getCenterScopedUrl(BASE_URL, "/api/external-assessments"), { headers: authHeaders });
                const json = await safeJson(res);
                const all = Array.isArray(json) ? json : (json?.content ?? []);
                data = all;
                setRows(all);
                setAllLoaded(true);
                setVisibleCount(all.length);
                store.setRows(CACHE_KEY, all, true);
            }
            if (!data.length) return flash("err", "No data to export");
            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "ExternalAssessment");
            XLSX.writeFile(wb, "external_assessment.xlsx");
            flash("ok", `Exported ${data.length.toLocaleString()} records`);
        } catch (e) {
            flash("err", "Export failed: " + e.message);
        } finally {
            setExporting(false);
        }
    };

    const downloadTemplate = () => {
        const a = document.createElement("a");
        a.href = "/templates/create_external_assessment_template.xlsx"; // local path
        a.download = "create_external_assessment_template.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    return (
        <div className="cmp-page">
            <div className="header">
                <h1>External Assessment</h1>
                {/* <button className="btn btn-primary" onClick={openCreate}>
                    Add Record
                </button> */}
            </div>

            {msg.text && (
                <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>{msg.text}</span>
                    <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
                </div>
            )}

            {uploadProgress && (
                <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 18px", marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: "#1e3a8a" }}>
                            {uploadProgress.error
                                ? `⚠ Stopped at chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}`
                                : uploading
                                    ? `Uploading chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}…`
                                    : `Upload complete`}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
                            {uploadProgress.uploaded.toLocaleString()} / {uploadProgress.total.toLocaleString()} rows
                            &nbsp;({Math.round((uploadProgress.uploaded / uploadProgress.total) * 100)}%)
                        </span>
                    </div>
                    <div style={{ height: 10, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
                        <div style={{
                            height: "100%",
                            width: `${(uploadProgress.uploaded / uploadProgress.total) * 100}%`,
                            background: uploadProgress.error ? "#ef4444" : "linear-gradient(90deg,#1e40af,#3b82f6)",
                            borderRadius: 999,
                            transition: "width 0.3s ease",
                        }} />
                    </div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
                        {uploadProgress.total.toLocaleString()} total rows · {uploadProgress.totalChunks} chunk{uploadProgress.totalChunks > 1 ? "s" : ""} of {CHUNK_SIZE} rows each
                    </div>
                </div>
            )}

            <div className="card grid-3">
                <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setFile(e.target.files[0] || null)}
                />

                <button className="btn btn-primary" onClick={uploadExcel} disabled={uploading}>
                    {uploading ? `Uploading chunk ${uploadProgress?.chunk ?? ""}/${uploadProgress?.totalChunks ?? ""}…` : "Upload Excel"}
                </button>

                <button className="btn btn-outline" onClick={downloadTemplate}>
                    Download Excel Template
                </button>
            </div>

            <div className="card">
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <input
                        className="search"
                        placeholder="Search beneficiary / assessor / agency / status..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ flex: "0 0 70%", marginBottom: 0 }}
                    />
                    <button
                        className="btn btn-outline"
                        onClick={exportExcel}
                        style={{ flex: "0 0 calc(30% - 8px)", fontSize: "0.8rem", padding: "6px 10px" }}
                        disabled={exporting}
                    >
                        {exporting ? "Processing..." : "Export All Data"}
                    </button>
                </div>
                {totalCount !== null && (
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginTop: 4, marginBottom: 8 }}>
                        Total: {totalCount.toLocaleString()}
                    </div>
                )}

                <div className="table-wrap">
                    <table className="ep-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                {/* <th>ID</th> */}
                                <th>Beneficiary ID</th>
                                <th>Beneficiary Name</th>
                                <th>Assessment</th>
                                <th>Attendance %</th>
                                <th>Assessment Conducted</th>
                                <th>Assessment Through Mode</th>
                                <th>Assessment Agency ID</th>
                                <th>Assessment Agency Name</th>
                                <th>Assessor ID</th>
                                <th>Assessor Name</th>
                                <th>Assessment Date</th>
                                <th>Beneficiary Assessment Status</th>
                                <th>Assessment Percentage</th>
                                <th>Batch ID</th>
                                <th>Center ID</th>
                                <th>Project ID</th>
                                <th className="actions">Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {filtered.slice(0, visibleCount).map((row, i) => (
                                <tr key={row.id || row.beneficiaryId}>
                                    <td>{i + 1}</td>
                                    {/* <td>{row.id ?? "-"}</td> */}
                                    <td>{row.beneficiaryId || "-"}</td>
                                    <td>{row.beneficiaryName || "-"}</td>
                                    <td>{yesNo(row.assessment)}</td>
                                    <td>{row.attendancePercentage ?? "-"}</td>
                                    <td>{yesNo(row.assessmentConducted)}</td>
                                    <td>{row.assessmentThroughMode || "-"}</td>
                                    <td>{row.assessmentAgencyId || "-"}</td>
                                    <td>{row.assessmentAgencyName || "-"}</td>
                                    <td>{row.assessorId || "-"}</td>
                                    <td>{row.assessorName || "-"}</td>
                                    <td>{toInputDate(row.assessmentDate) || "-"}</td>
                                    <td>{row.beneficiaryAssessmentStatus || "-"}</td>
                                    <td>{row.assessmentPercentage ?? "-"}</td>
                                    <td>{row.batchId || "-"}</td>
                                    <td>{row.centerId || "-"}</td>
                                    <td>{row.projectId || "-"}</td>
                                    <td className="actions">
                                        {/* <button
                                            className="btn btn-sm"
                                            onClick={() => openEdit(row.beneficiaryId)}
                                        >
                                            Edit
                                        </button> */}
                                        <button
                                            className="btn btn-sm btn-danger"
                                            onClick={() => deleteExternalAssessment(row.id)}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}

                            {!filtered.length && (
                                <tr>
                                    <td colSpan="19" className="muted">
                                        {loading ? "Loading..." : "No records found"}
                                    </td>
                                </tr>
                            )}
                            {!allLoaded && !loading && (
                                <tr>
                                    <td colSpan="19" style={{ textAlign: "center", padding: "14px" }}>
                                        <button className="btn btn-outline" onClick={fetchAllExternalAssessments} disabled={loadingAll}>
                                            {loadingAll ? "Loading all data…" : "Load All Data"}
                                        </button>
                                    </td>
                                </tr>
                            )}
                            {filtered.length > visibleCount && (
                                <tr>
                                    <td colSpan="19" style={{ textAlign: "center", padding: "14px" }}>
                                        <button
                                            className="btn btn-outline"
                                            onClick={() => setVisibleCount((c) => c + 100)}
                                        >
                                            Load More ({filtered.length - visibleCount} remaining)
                                        </button>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {modalOpen && createPortal(
                <div className="modal-overlay" onClick={() => {
                    setModalOpen(false);
                    resetForm();
                }}>
                    <div className="ep-modal wide" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-title">
                            {editingBeneficiaryId ? "Edit External Assessment" : "Add External Assessment"}
                        </div>

                        <div className="form-grid">
                            <div className="field">
                                <label>Beneficiary ID</label>
                                <input
                                    name="beneficiaryId"
                                    value={form.beneficiaryId}
                                    onChange={handleFormChange}
                                    disabled={!!editingBeneficiaryId}
                                    placeholder="EP/CN-00001"
                                />
                            </div>

                            <div className="field">
                                <label>Assessment</label>
                                <select
                                    name="assessment"
                                    value={String(form.assessment)}
                                    onChange={handleFormChange}
                                >
                                    <option value="false">No</option>
                                    <option value="true">Yes</option>
                                </select>
                            </div>

                            <div className="field">
                                <label>Attendance Percentage</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    name="attendancePercentage"
                                    value={form.attendancePercentage}
                                    onChange={handleFormChange}
                                    placeholder="70"
                                />
                            </div>

                            <div className="field">
                                <label>Assessment Conducted</label>
                                <select
                                    name="assessmentConducted"
                                    value={String(form.assessmentConducted)}
                                    onChange={handleFormChange}
                                >
                                    <option value="false">No</option>
                                    <option value="true">Yes</option>
                                </select>
                            </div>

                            <div className="field">
                                <label>Assessment Through Mode</label>
                                <input
                                    name="assessmentThroughMode"
                                    value={form.assessmentThroughMode}
                                    onChange={handleFormChange}
                                    placeholder="Self"
                                />
                            </div>

                            <div className="field">
                                <label>Assessment Agency ID</label>
                                <input
                                    name="assessmentAgencyId"
                                    value={form.assessmentAgencyId}
                                    onChange={handleFormChange}
                                    placeholder="AA-2001"
                                />
                            </div>

                            <div className="field">
                                <label>Assessment Agency Name</label>
                                <input
                                    name="assessmentAgencyName"
                                    value={form.assessmentAgencyName}
                                    onChange={handleFormChange}
                                    placeholder="XYZ Assessment Agency"
                                />
                            </div>

                            <div className="field">
                                <label>Assessor ID</label>
                                <input
                                    name="assessorId"
                                    value={form.assessorId}
                                    onChange={handleFormChange}
                                    placeholder="EP/AR-00001"
                                />
                            </div>

                            <div className="field">
                                <label>Assessment Date</label>
                                <input
                                    type="date"
                                    name="assessmentDate"
                                    value={form.assessmentDate}
                                    onChange={handleFormChange}
                                />
                            </div>

                            <div className="field">
                                <label>Beneficiary Assessment Status</label>
                                <input
                                    name="beneficiaryAssessmentStatus"
                                    value={form.beneficiaryAssessmentStatus}
                                    onChange={handleFormChange}
                                    placeholder="Pass"
                                />
                            </div>

                            <div className="field">
                                <label>Assessment Percentage</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    name="assessmentPercentage"
                                    value={form.assessmentPercentage}
                                    onChange={handleFormChange}
                                    placeholder="83"
                                />
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button
                                className="btn"
                                onClick={() => {
                                    setModalOpen(false);
                                    resetForm();
                                }}
                            >
                                Cancel
                            </button>
                            <button className="btn btn-primary" onClick={saveExternalAssessment} disabled={saving}>
                                {saving ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            <style>{`
.cmp-page{
padding:22px;
background:#f7f7fb;
min-height:100vh;
font-family:Inter
}

.header{
display:flex;
justify-content:space-between;
align-items:center;
gap:12px
}

h1{
font-size:26px;
font-weight:900
}

.flash{
padding:10px;
border-radius:10px;
margin:10px 0;
font-weight:700
}

.flash-ok{
background:#ecfdf5;
color:#065f46
}

.flash-err{
background:#fef2f2;
color:#991b1b
}

.card{
background:#fff;
border:1px solid #e5e7eb;
border-radius:14px;
padding:16px;
margin-bottom:14px
}

.grid-3{
display:grid;
grid-template-columns:1fr auto auto;
gap:10px
}

.btn{
border:1px solid #e5e7eb;
padding:10px 14px;
border-radius:12px;
font-weight:800;
cursor:pointer;
background:#fff
}

.btn:disabled{
opacity:.65;
cursor:not-allowed
}

.btn-primary{
background:#2563eb;
color:#fff
}

.btn-outline{
background:#fff
}

.btn-sm{
padding:6px 10px;
font-size:12px;
margin-right:8px
}

.btn-danger{
color:#991b1b;
border-color:#fca5a5
}

.search{
margin-bottom:10px;
padding:10px;
border-radius:12px;
border:1px solid #e5e7eb;
width:100%
}

.table-wrap{
overflow-x:auto
}

.ep-table{
width:100%;
min-width:2200px;
border-collapse:collapse
}

th,td{
padding:10px;
border-bottom:1px solid #e5e7eb;
font-size:13px;
white-space:nowrap;
vertical-align:top
}

th{
background:#fcfcff;
text-align:left
}

td.actions{
position:sticky;
right:0;
background:#fff
}

.muted{
color:#6b7280;
font-weight:700;
text-align:center
}

.modal-overlay{
position:fixed;
inset:0;
background:rgba(0,0,0,.45);
display:flex;
align-items:center;
justify-content:center;
z-index:99999
}

.ep-modal{
background:#fff;
padding:18px;
border-radius:16px;
width:520px;
max-height:90vh;
overflow:auto
}

.ep-modal.wide{
width:min(1000px,92vw)
}

.modal-title{
font-size:18px;
font-weight:900;
margin-bottom:14px
}

.form-grid{
display:grid;
grid-template-columns:repeat(2, minmax(0, 1fr));
gap:12px
}

.field{
display:flex;
flex-direction:column;
gap:6px
}

.field label{
font-size:12px;
font-weight:800;
color:#374151
}

.ep-modal input,
.ep-modal select{
width:100%;
padding:10px;
border-radius:12px;
border:1px solid #e5e7eb
}

.modal-actions{
display:flex;
justify-content:flex-end;
gap:10px;
margin-top:16px
}

@media (max-width: 768px){
  .grid-3{
    grid-template-columns:1fr
  }

  .form-grid{
    grid-template-columns:1fr
  }

  .btn-sm{
    margin-right:6px;
    margin-bottom:6px
  }
}
`}</style>
        </div>
    );
}



// import React, { useCallback, useEffect, useMemo, useState } from "react";
// import { createPortal } from "react-dom";
// import * as XLSX from "xlsx";
// import { filterRowsForCurrentScope, getCenterScopedUrl } from "../../utils/centerScope";
// import useDataStore from "../../store/dataStore";

// const BASE_URL = "https://mis.empowerplus.in";

// const emptyForm = {
//     beneficiaryId: "",
//     assessment: false,
//     attendancePercentage: "",
//     assessmentConducted: false,
//     assessmentThroughMode: "",
//     assessmentAgencyId: "",
//     assessmentAgencyName: "",
//     assessorId: "",
//     assessmentDate: "",
//     beneficiaryAssessmentStatus: "",
//     assessmentPercentage: ""
// };

// const yesNo = (value) => (value ? "Yes" : "No");

// const toInputDate = (value) => {
//     if (!value) return "";
//     return String(value).slice(0, 10);
// };

// const numberOrNull = (value) => {
//     if (value === "" || value === null || value === undefined) return null;
//     const parsed = Number(value);
//     return Number.isNaN(parsed) ? null : parsed;
// };

// export default function ExternalAssessment() {
//     const token = localStorage.getItem("token");
//     const store = useDataStore();
//     const CACHE_KEY = "externalAssessments";

//     const [msg, setMsg] = useState({ type: "", text: "" });
//     const [rows, setRows] = useState(() => store.getRows(CACHE_KEY));
//     const [loading, setLoading] = useState(false);
//     const [search, setSearch] = useState("");
//     const [visibleCount, setVisibleCount] = useState(100);
//     const [file, setFile] = useState(null);
//     const [uploading, setUploading] = useState(false);
//     const [uploadProgress, setUploadProgress] = useState(null);
//     const [totalCount, setTotalCount] = useState(null);
//     const [modalOpen, setModalOpen] = useState(false);
//     const [saving, setSaving] = useState(false);
//     const [editingBeneficiaryId, setEditingBeneficiaryId] = useState("");
//     const [form, setForm] = useState(emptyForm);

//     const flash = (type, text) => {
//         setMsg({ type, text });
//     };

//     const authHeaders = useMemo(
//         () => ({
//             Authorization: `Bearer ${token}`
//         }),
//         [token]
//     );

//     const jsonHeaders = useMemo(
//         () => ({
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//         }),
//         [token]
//     );

//     const safeJson = async (res) => {
//         const text = await res.text();
//         try {
//             return text ? JSON.parse(text) : [];
//         } catch {
//             return [];
//         }
//     };

//     const fetchExternalAssessments = useCallback(async () => {
//         if (store.isCached(CACHE_KEY)) {
//             setRows(store.getRows(CACHE_KEY));
//             return;
//         }
//         setLoading(true);

//         try {
//             const res = await fetch(getCenterScopedUrl(BASE_URL, "/api/external-assessments"), {
//                 headers: authHeaders
//             });

//             const data = await safeJson(res);
//             const list = data.content || data || [];
//             setRows(list);
//             store.setRows(CACHE_KEY, list, true);
//         } catch {
//             setRows([]);
//             flash("err", "Failed to load external assessments");
//         } finally {
//             setLoading(false);
//         }
//     }, [authHeaders]);

//     useEffect(() => {
//         fetchExternalAssessments();
//     }, [fetchExternalAssessments]);

//     useEffect(() => {
//         fetch(`${BASE_URL}/api/dashboard/counts`, { headers: authHeaders })
//             .then(r => r.ok ? r.json() : null)
//             .then(d => { if (d) setTotalCount(d.externalAssessments ?? null); })
//             .catch(() => {});
//     }, []);

//     useEffect(() => { setVisibleCount(100); }, [search]);

//     const scopedRows = useMemo(() => filterRowsForCurrentScope(rows), [rows]);

//     const filtered = useMemo(() => {
//         const q = search.trim().toLowerCase();

//         if (!q) return scopedRows;

//         return scopedRows.filter((row) =>
//             row.beneficiaryId?.toLowerCase().includes(q) ||
//             row.beneficiaryName?.toLowerCase().includes(q) ||
//             row.assessorId?.toLowerCase().includes(q) ||
//             row.assessorName?.toLowerCase().includes(q) ||
//             row.assessmentAgencyName?.toLowerCase().includes(q) ||
//             row.beneficiaryAssessmentStatus?.toLowerCase().includes(q)
//         );
//     }, [scopedRows, search]);

//     const resetForm = () => {
//         setForm(emptyForm);
//         setEditingBeneficiaryId("");
//     };

//     const openCreate = () => {
//         resetForm();
//         setModalOpen(true);
//     };

//     const openEdit = async (beneficiaryId) => {
//         if (!beneficiaryId) return;

//         setSaving(false);
//         setModalOpen(true);
//         setEditingBeneficiaryId(beneficiaryId);

//         try {
//             const res = await fetch(
//                 `${BASE_URL}/api/external-assessments/${encodeURIComponent(beneficiaryId)}`,
//                 { headers: authHeaders }
//             );

//             if (!res.ok) {
//                 throw new Error("Failed to fetch record");
//             }

//             const data = await safeJson(res);

//             setForm({
//                 beneficiaryId: data.beneficiaryId || "",
//                 assessment: !!data.assessment,
//                 attendancePercentage: data.attendancePercentage ?? "",
//                 assessmentConducted: !!data.assessmentConducted,
//                 assessmentThroughMode: data.assessmentThroughMode || "",
//                 assessmentAgencyId: data.assessmentAgencyId || "",
//                 assessmentAgencyName: data.assessmentAgencyName || "",
//                 assessorId: data.assessorId || "",
//                 assessmentDate: toInputDate(data.assessmentDate),
//                 beneficiaryAssessmentStatus: data.beneficiaryAssessmentStatus || "",
//                 assessmentPercentage: data.assessmentPercentage ?? ""
//             });
//         } catch {
//             flash("err", "Failed to load record details");
//             setModalOpen(false);
//             resetForm();
//         }
//     };

//     const handleFormChange = (event) => {
//         const { name, value } = event.target;

//         if (name === "assessment" || name === "assessmentConducted") {
//             setForm((prev) => ({
//                 ...prev,
//                 [name]: value === "true"
//             }));
//             return;
//         }

//         setForm((prev) => ({
//             ...prev,
//             [name]: value
//         }));
//     };

//     const saveExternalAssessment = async () => {
//         if (!form.beneficiaryId.trim()) {
//             flash("err", "Beneficiary ID is required");
//             return;
//         }

//         setSaving(true);

//         try {
//             const payload = {
//                 beneficiaryId: form.beneficiaryId.trim(),
//                 assessment: !!form.assessment,
//                 attendancePercentage: numberOrNull(form.attendancePercentage),
//                 assessmentConducted: !!form.assessmentConducted,
//                 assessmentThroughMode: form.assessmentThroughMode.trim(),
//                 assessmentAgencyId: form.assessmentAgencyId.trim(),
//                 assessmentAgencyName: form.assessmentAgencyName.trim(),
//                 assessorId: form.assessorId.trim(),
//                 assessmentDate: form.assessmentDate || null,
//                 beneficiaryAssessmentStatus: form.beneficiaryAssessmentStatus.trim(),
//                 assessmentPercentage: numberOrNull(form.assessmentPercentage)
//             };

//             const res = await fetch(`${BASE_URL}/api/external-assessments`, {
//                 method: "POST",
//                 headers: jsonHeaders,
//                 body: JSON.stringify(payload)
//             });

//             if (!res.ok) {
//                 throw new Error("Save failed");
//             }

//             flash("ok", editingBeneficiaryId ? "External assessment updated" : "External assessment created");
//             setModalOpen(false);
//             resetForm();
//             store.invalidate(CACHE_KEY);
//             fetchExternalAssessments();
//         } catch {
//             flash("err", "Failed to save external assessment");
//         } finally {
//             setSaving(false);
//         }
//     };

//     const deleteExternalAssessment = async (id) => {
//         if (!id) {
//             flash("err", "Record ID not found");
//             return;
//         }

//         if (!window.confirm("Delete this external assessment record?")) return;

//         try {
//             const res = await fetch(`${BASE_URL}/api/external-assessments/${id}`, {
//                 method: "DELETE",
//                 headers: authHeaders
//             });

//             if (!res.ok) {
//                 throw new Error("Delete failed");
//             }

//             flash("ok", "External assessment deleted");
//             store.invalidate(CACHE_KEY);
//             fetchExternalAssessments();
//         } catch {
//             flash("err", "Failed to delete record");
//         }
//     };

//     const CHUNK_SIZE = 5000;
//     const PARALLEL = 6;

//     const uploadExcel = async () => {
//         if (!file) return flash("err", "Select Excel file");
//         let dataRows;
//         try {
//             const buffer = await file.arrayBuffer();
//             const wb = XLSX.read(buffer, { type: "array" });
//             const ws = wb.Sheets[wb.SheetNames[0]];
//             const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
//             dataRows = raw.slice(1);
//         } catch (err) {
//             return flash("err", `Cannot read file: ${err.message}`);
//         }
//         if (!dataRows.length) return flash("err", "Excel file is empty");
//         const totalRows = dataRows.length;
//         const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);
//         setUploading(true);
//         setUploadProgress({ uploaded: 0, total: totalRows, chunk: 0, totalChunks });
//         let uploadedSoFar = 0;
//         let totalInserted = 0;
//         let totalSkipped = 0;
//         let failed = false;
//         let firstError = "";
//         const uploadOneChunk = async (i) => {
//             const chunkRows = dataRows.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
//             const res = await fetch(`${BASE_URL}/api/external-assessments/upload-json?actor=Rahul`, {
//                 method: "POST",
//                 headers: { ...authHeaders, "Content-Type": "application/json" },
//                 body: JSON.stringify(chunkRows),
//             });
//             if (res.status === 413) throw new Error(`Chunk ${i + 1}: Payload too large (413)`);
//             const text = await res.text();
//             let data;
//             try { data = JSON.parse(text); } catch { data = text; }
//             if (!res.ok) throw new Error(`Chunk ${i + 1}: ${data?.message || `HTTP ${res.status}`}`);
//             return { inserted: data?.inserted ?? 0, skipped: data?.skipped ?? 0 };
//         };
//         for (let batchStart = 0; batchStart < totalChunks && !failed; batchStart += PARALLEL) {
//             const batchEnd = Math.min(batchStart + PARALLEL, totalChunks);
//             const batchIndices = Array.from({ length: batchEnd - batchStart }, (_, k) => batchStart + k);
//             setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks });
//             const results = await Promise.allSettled(batchIndices.map(uploadOneChunk));
//             for (const result of results) {
//                 if (result.status === "fulfilled") {
//                     totalInserted += result.value.inserted;
//                     totalSkipped += result.value.skipped;
//                     uploadedSoFar += result.value.inserted + result.value.skipped;
//                 } else {
//                     failed = true;
//                     firstError = result.reason?.message || "Upload failed";
//                 }
//             }
//             setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks, error: failed });
//         }
//         setUploading(false);
//         if (!failed) {
//             flash("ok", `Upload complete — Inserted: ${totalInserted.toLocaleString()}, Skipped: ${totalSkipped.toLocaleString()} (${totalChunks} chunks)`);
//             setFile(null);
//             setUploadProgress(null);
//             store.invalidate(CACHE_KEY);
//             fetchExternalAssessments();
//         } else {
//             flash("err", firstError);
//             setUploadProgress(prev => prev ? { ...prev, error: true } : null);
//         }
//     };

//     // const downloadTemplate = () => {
//     //     const rowsForTemplate = [
//     //         [
//     //             "Beneficiary ID",
//     //             "Beneficiary Name",
//     //             "Assessment (Yes/No)",
//     //             "Attendance (%)",
//     //             "Assessment conducted (Yes/No)",
//     //             "Assessment through (Mode)",
//     //             "Assessment Agency ID",
//     //             "Assessment Agency Name",
//     //             "Assessor ID",
//     //             "Assessor Name",
//     //             "Assessment Date",
//     //             "Beneficiary Assessment Status",
//     //             "Assessment Percentage"
//     //         ],
//     //         [
//     //             "EP/CN-00001",
//     //             "Rahul Kumar",
//     //             "Yes",
//     //             70,
//     //             "Yes",
//     //             "Self",
//     //             "AA-2001",
//     //             "XYZ Assessment Agency",
//     //             "EP/AR-00001",
//     //             "Ramesh Sir",
//     //             "23/03/2024",
//     //             "Pass",
//     //             83
//     //         ]
//     //     ];

//     //     const worksheet = XLSX.utils.aoa_to_sheet(rowsForTemplate);
//     //     const workbook = XLSX.utils.book_new();
//     //     XLSX.utils.book_append_sheet(workbook, worksheet, "External Assessment");
//     //     XLSX.writeFile(workbook, "external_assessment_template.xlsx");
//     // };


//     const [exporting, setExporting] = useState(false);

//     const exportExcel = () => {
//         if (!rows.length) return flash("err", "No data to export");
//         if (exporting) return;
//         setExporting(true);
//         setTimeout(() => {
//             try {
//                 const ws = XLSX.utils.json_to_sheet(rows);
//                 const wb = XLSX.utils.book_new();
//                 XLSX.utils.book_append_sheet(wb, ws, "ExternalAssessment");
//                 XLSX.writeFile(wb, "external_assessment.xlsx");
//             } finally {
//                 setExporting(false);
//             }
//         }, 50);
//     };

//     const downloadTemplate = () => {
//         const a = document.createElement("a");
//         a.href = "/templates/create_external_assessment_template.xlsx"; // local path
//         a.download = "create_external_assessment_template.xlsx";
//         document.body.appendChild(a);
//         a.click();
//         a.remove();
//     };

//     return (
//         <div className="cmp-page">
//             <div className="header">
//                 <h1>External Assessment</h1>
//                 {/* <button className="btn btn-primary" onClick={openCreate}>
//                     Add Record
//                 </button> */}
//             </div>

//             {msg.text && (
//                 <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
//                     <span>{msg.text}</span>
//                     <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
//                 </div>
//             )}

//             {uploadProgress && (
//                 <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 18px", marginBottom: 8 }}>
//                     <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
//                         <span style={{ fontSize: 13, fontWeight: 800, color: "#1e3a8a" }}>
//                             {uploadProgress.error
//                                 ? `⚠ Stopped at chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}`
//                                 : uploading
//                                     ? `Uploading chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}…`
//                                     : `Upload complete`}
//                         </span>
//                         <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
//                             {uploadProgress.uploaded.toLocaleString()} / {uploadProgress.total.toLocaleString()} rows
//                             &nbsp;({Math.round((uploadProgress.uploaded / uploadProgress.total) * 100)}%)
//                         </span>
//                     </div>
//                     <div style={{ height: 10, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
//                         <div style={{
//                             height: "100%",
//                             width: `${(uploadProgress.uploaded / uploadProgress.total) * 100}%`,
//                             background: uploadProgress.error ? "#ef4444" : "linear-gradient(90deg,#1e40af,#3b82f6)",
//                             borderRadius: 999,
//                             transition: "width 0.3s ease",
//                         }} />
//                     </div>
//                     <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
//                         {uploadProgress.total.toLocaleString()} total rows · {uploadProgress.totalChunks} chunk{uploadProgress.totalChunks > 1 ? "s" : ""} of {CHUNK_SIZE} rows each
//                     </div>
//                 </div>
//             )}

//             <div className="card grid-3">
//                 <input
//                     type="file"
//                     accept=".xlsx,.xls"
//                     onChange={(e) => setFile(e.target.files[0] || null)}
//                 />

//                 <button className="btn btn-primary" onClick={uploadExcel} disabled={uploading}>
//                     {uploading ? `Uploading chunk ${uploadProgress?.chunk ?? ""}/${uploadProgress?.totalChunks ?? ""}…` : "Upload Excel"}
//                 </button>

//                 <button className="btn btn-outline" onClick={downloadTemplate}>
//                     Download Excel Template
//                 </button>
//             </div>

//             <div className="card">
//                 <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
//                     <input
//                         className="search"
//                         placeholder="Search beneficiary / assessor / agency / status..."
//                         value={search}
//                         onChange={(e) => setSearch(e.target.value)}
//                         style={{ flex: "0 0 70%", marginBottom: 0 }}
//                     />
//                     <button
//                         className="btn btn-outline"
//                         onClick={exportExcel}
//                         style={{ flex: "0 0 calc(30% - 8px)", fontSize: "0.8rem", padding: "6px 10px" }}
//                         disabled={exporting}
//                     >
//                         {exporting ? "Processing..." : "Export All Data"}
//                     </button>
//                 </div>
//                 {totalCount !== null && (
//                     <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginTop: 4, marginBottom: 8 }}>
//                         Total: {totalCount.toLocaleString()}
//                     </div>
//                 )}

//                 <div className="table-wrap">
//                     <table className="ep-table">
//                         <thead>
//                             <tr>
//                                 <th>#</th>
//                                 <th>ID</th>
//                                 <th>Beneficiary ID</th>
//                                 <th>Beneficiary Name</th>
//                                 <th>Assessment</th>
//                                 <th>Attendance %</th>
//                                 <th>Assessment Conducted</th>
//                                 <th>Assessment Through Mode</th>
//                                 <th>Assessment Agency ID</th>
//                                 <th>Assessment Agency Name</th>
//                                 <th>Assessor ID</th>
//                                 <th>Assessor Name</th>
//                                 <th>Assessment Date</th>
//                                 <th>Beneficiary Assessment Status</th>
//                                 <th>Assessment Percentage</th>
//                                 <th>Batch ID</th>
//                                 <th>Center ID</th>
//                                 <th>Project ID</th>
//                                 <th className="actions">Actions</th>
//                             </tr>
//                         </thead>

//                         <tbody>
//                             {filtered.slice(0, visibleCount).map((row, i) => (
//                                 <tr key={row.id || row.beneficiaryId}>
//                                     <td>{i + 1}</td>
//                                     <td>{row.id ?? "-"}</td>
//                                     <td>{row.beneficiaryId || "-"}</td>
//                                     <td>{row.beneficiaryName || "-"}</td>
//                                     <td>{yesNo(row.assessment)}</td>
//                                     <td>{row.attendancePercentage ?? "-"}</td>
//                                     <td>{yesNo(row.assessmentConducted)}</td>
//                                     <td>{row.assessmentThroughMode || "-"}</td>
//                                     <td>{row.assessmentAgencyId || "-"}</td>
//                                     <td>{row.assessmentAgencyName || "-"}</td>
//                                     <td>{row.assessorId || "-"}</td>
//                                     <td>{row.assessorName || "-"}</td>
//                                     <td>{toInputDate(row.assessmentDate) || "-"}</td>
//                                     <td>{row.beneficiaryAssessmentStatus || "-"}</td>
//                                     <td>{row.assessmentPercentage ?? "-"}</td>
//                                     <td>{row.batchId || "-"}</td>
//                                     <td>{row.centerId || "-"}</td>
//                                     <td>{row.projectId || "-"}</td>
//                                     <td className="actions">
//                                         {/* <button
//                                             className="btn btn-sm"
//                                             onClick={() => openEdit(row.beneficiaryId)}
//                                         >
//                                             Edit
//                                         </button> */}
//                                         <button
//                                             className="btn btn-sm btn-danger"
//                                             onClick={() => deleteExternalAssessment(row.id)}
//                                         >
//                                             Delete
//                                         </button>
//                                     </td>
//                                 </tr>
//                             ))}

//                             {!filtered.length && (
//                                 <tr>
//                                     <td colSpan="19" className="muted">
//                                         {loading ? "Loading..." : "No records found"}
//                                     </td>
//                                 </tr>
//                             )}
//                             {filtered.length > visibleCount && (
//                                 <tr>
//                                     <td colSpan="19" style={{ textAlign: "center", padding: "14px" }}>
//                                         <button
//                                             className="btn btn-outline"
//                                             onClick={() => setVisibleCount((c) => c + 100)}
//                                         >
//                                             Load More ({filtered.length - visibleCount} remaining)
//                                         </button>
//                                     </td>
//                                 </tr>
//                             )}
//                         </tbody>
//                     </table>
//                 </div>
//             </div>

//             {modalOpen && createPortal(
//                 <div className="modal-overlay" onClick={() => {
//                     setModalOpen(false);
//                     resetForm();
//                 }}>
//                     <div className="ep-modal wide" onClick={(e) => e.stopPropagation()}>
//                         <div className="modal-title">
//                             {editingBeneficiaryId ? "Edit External Assessment" : "Add External Assessment"}
//                         </div>

//                         <div className="form-grid">
//                             <div className="field">
//                                 <label>Beneficiary ID</label>
//                                 <input
//                                     name="beneficiaryId"
//                                     value={form.beneficiaryId}
//                                     onChange={handleFormChange}
//                                     disabled={!!editingBeneficiaryId}
//                                     placeholder="EP/CN-00001"
//                                 />
//                             </div>

//                             <div className="field">
//                                 <label>Assessment</label>
//                                 <select
//                                     name="assessment"
//                                     value={String(form.assessment)}
//                                     onChange={handleFormChange}
//                                 >
//                                     <option value="false">No</option>
//                                     <option value="true">Yes</option>
//                                 </select>
//                             </div>

//                             <div className="field">
//                                 <label>Attendance Percentage</label>
//                                 <input
//                                     type="number"
//                                     min="0"
//                                     max="100"
//                                     name="attendancePercentage"
//                                     value={form.attendancePercentage}
//                                     onChange={handleFormChange}
//                                     placeholder="70"
//                                 />
//                             </div>

//                             <div className="field">
//                                 <label>Assessment Conducted</label>
//                                 <select
//                                     name="assessmentConducted"
//                                     value={String(form.assessmentConducted)}
//                                     onChange={handleFormChange}
//                                 >
//                                     <option value="false">No</option>
//                                     <option value="true">Yes</option>
//                                 </select>
//                             </div>

//                             <div className="field">
//                                 <label>Assessment Through Mode</label>
//                                 <input
//                                     name="assessmentThroughMode"
//                                     value={form.assessmentThroughMode}
//                                     onChange={handleFormChange}
//                                     placeholder="Self"
//                                 />
//                             </div>

//                             <div className="field">
//                                 <label>Assessment Agency ID</label>
//                                 <input
//                                     name="assessmentAgencyId"
//                                     value={form.assessmentAgencyId}
//                                     onChange={handleFormChange}
//                                     placeholder="AA-2001"
//                                 />
//                             </div>

//                             <div className="field">
//                                 <label>Assessment Agency Name</label>
//                                 <input
//                                     name="assessmentAgencyName"
//                                     value={form.assessmentAgencyName}
//                                     onChange={handleFormChange}
//                                     placeholder="XYZ Assessment Agency"
//                                 />
//                             </div>

//                             <div className="field">
//                                 <label>Assessor ID</label>
//                                 <input
//                                     name="assessorId"
//                                     value={form.assessorId}
//                                     onChange={handleFormChange}
//                                     placeholder="EP/AR-00001"
//                                 />
//                             </div>

//                             <div className="field">
//                                 <label>Assessment Date</label>
//                                 <input
//                                     type="date"
//                                     name="assessmentDate"
//                                     value={form.assessmentDate}
//                                     onChange={handleFormChange}
//                                 />
//                             </div>

//                             <div className="field">
//                                 <label>Beneficiary Assessment Status</label>
//                                 <input
//                                     name="beneficiaryAssessmentStatus"
//                                     value={form.beneficiaryAssessmentStatus}
//                                     onChange={handleFormChange}
//                                     placeholder="Pass"
//                                 />
//                             </div>

//                             <div className="field">
//                                 <label>Assessment Percentage</label>
//                                 <input
//                                     type="number"
//                                     min="0"
//                                     max="100"
//                                     name="assessmentPercentage"
//                                     value={form.assessmentPercentage}
//                                     onChange={handleFormChange}
//                                     placeholder="83"
//                                 />
//                             </div>
//                         </div>

//                         <div className="modal-actions">
//                             <button
//                                 className="btn"
//                                 onClick={() => {
//                                     setModalOpen(false);
//                                     resetForm();
//                                 }}
//                             >
//                                 Cancel
//                             </button>
//                             <button className="btn btn-primary" onClick={saveExternalAssessment} disabled={saving}>
//                                 {saving ? "Saving..." : "Save"}
//                             </button>
//                         </div>
//                     </div>
//                 </div>,
//                 document.body
//             )}

//             <style>{`
// .cmp-page{
// padding:22px;
// background:#f7f7fb;
// min-height:100vh;
// font-family:Inter
// }

// .header{
// display:flex;
// justify-content:space-between;
// align-items:center;
// gap:12px
// }

// h1{
// font-size:26px;
// font-weight:900
// }

// .flash{
// padding:10px;
// border-radius:10px;
// margin:10px 0;
// font-weight:700
// }

// .flash-ok{
// background:#ecfdf5;
// color:#065f46
// }

// .flash-err{
// background:#fef2f2;
// color:#991b1b
// }

// .card{
// background:#fff;
// border:1px solid #e5e7eb;
// border-radius:14px;
// padding:16px;
// margin-bottom:14px
// }

// .grid-3{
// display:grid;
// grid-template-columns:1fr auto auto;
// gap:10px
// }

// .btn{
// border:1px solid #e5e7eb;
// padding:10px 14px;
// border-radius:12px;
// font-weight:800;
// cursor:pointer;
// background:#fff
// }

// .btn:disabled{
// opacity:.65;
// cursor:not-allowed
// }

// .btn-primary{
// background:#2563eb;
// color:#fff
// }

// .btn-outline{
// background:#fff
// }

// .btn-sm{
// padding:6px 10px;
// font-size:12px;
// margin-right:8px
// }

// .btn-danger{
// color:#991b1b;
// border-color:#fca5a5
// }

// .search{
// margin-bottom:10px;
// padding:10px;
// border-radius:12px;
// border:1px solid #e5e7eb;
// width:100%
// }

// .table-wrap{
// overflow-x:auto
// }

// .ep-table{
// width:100%;
// min-width:2200px;
// border-collapse:collapse
// }

// th,td{
// padding:10px;
// border-bottom:1px solid #e5e7eb;
// font-size:13px;
// white-space:nowrap;
// vertical-align:top
// }

// th{
// background:#fcfcff;
// text-align:left
// }

// td.actions{
// position:sticky;
// right:0;
// background:#fff
// }

// .muted{
// color:#6b7280;
// font-weight:700;
// text-align:center
// }

// .modal-overlay{
// position:fixed;
// inset:0;
// background:rgba(0,0,0,.45);
// display:flex;
// align-items:center;
// justify-content:center;
// z-index:99999
// }

// .ep-modal{
// background:#fff;
// padding:18px;
// border-radius:16px;
// width:520px;
// max-height:90vh;
// overflow:auto
// }

// .ep-modal.wide{
// width:min(1000px,92vw)
// }

// .modal-title{
// font-size:18px;
// font-weight:900;
// margin-bottom:14px
// }

// .form-grid{
// display:grid;
// grid-template-columns:repeat(2, minmax(0, 1fr));
// gap:12px
// }

// .field{
// display:flex;
// flex-direction:column;
// gap:6px
// }

// .field label{
// font-size:12px;
// font-weight:800;
// color:#374151
// }

// .ep-modal input,
// .ep-modal select{
// width:100%;
// padding:10px;
// border-radius:12px;
// border:1px solid #e5e7eb
// }

// .modal-actions{
// display:flex;
// justify-content:flex-end;
// gap:10px;
// margin-top:16px
// }

// @media (max-width: 768px){
//   .grid-3{
//     grid-template-columns:1fr
//   }

//   .form-grid{
//     grid-template-columns:1fr
//   }

//   .btn-sm{
//     margin-right:6px;
//     margin-bottom:6px
//   }
// }
// `}</style>
//         </div>
//     );
// }
